<script setup lang="ts">
// Two-factor sign-in (central totp.go): set up with an authenticator app
// (the password, then the app's QR code and a code from it), the recovery
// codes shown once, and off again for a user who need not have it.
import { computed, ref } from 'vue'
import { central, type CentralUser } from '../api'
import { useNode } from '../store'

const props = defineProps<{ user: CentralUser }>()
const emit = defineEmits<{ done: [] }>()
const node = useNode()
const step = ref<'' | 'password' | 'scan' | 'codes' | 'off'>('')
const password = ref('')
const code = ref('')
const secret = ref('')
const qr = ref('') // the otpauth link as a picture
const codes = ref<string[]>([])
const err = ref('')
const busy = ref(false)
// superadmins must have it (they can not turn it off; another can reset it)
const required = computed(() => props.user.superadmin)

async function run(f: () => Promise<void>) {
    err.value = ''
    busy.value = true
    try {
        await f()
    } catch (e) {
        err.value = e instanceof Error ? e.message : String(e)
    } finally {
        busy.value = false
    }
}
const start = () =>
    run(async () => {
        const r = await central.totpSetup(password.value)
        secret.value = r.secret
        // the QR code library loads only here, with the rest of this screen
        const qrcode = (await import('qrcode-generator')).default
        const q = qrcode(0, 'M')
        q.addData(r.uri)
        q.make()
        qr.value = q.createDataURL(5, 8)
        password.value = ''
        step.value = 'scan'
    })
const enable = () =>
    run(async () => {
        const r = await central.totpEnable(code.value.trim())
        codes.value = r.recovery_codes
        code.value = ''
        step.value = 'codes'
    })
function finish() {
    if (node.user) node.user = { ...node.user, totp: true, totp_enrol: false }
    step.value = ''
    codes.value = []
    emit('done')
}
const disable = () =>
    run(async () => {
        await central.totpDisable(password.value, code.value.trim())
        if (node.user) node.user = { ...node.user, totp: false }
        password.value = code.value = ''
        step.value = ''
    })
const grouped = computed(() => secret.value.replace(/(.{4})/g, '$1 ').trim())
function copyCodes() {
    void node.copy(codes.value.join('\n'))
}
</script>

<template>
    <div data-test="two-factor">
        <template v-if="!step">
            <p class="mt-0 mb-2 text-fg-2">
                <template v-if="user.totp">Two-factor sign-in is <b class="text-ok">on</b>.</template>
                <template v-else
                    >Two-factor sign-in is off: a code from an authenticator app (Google Authenticator, 1Password,
                    Authy…) after the password.{{ required ? ' Superadmins must have it.' : '' }}</template
                >
            </p>
            <button v-if="!user.totp" type="button" data-test="totp-start" @click="step = 'password'">Set up</button>
            <button v-else-if="!required" type="button" class="btn-ghost" @click="step = 'off'">Turn off</button>
            <p v-else class="mt-1 mb-0 text-xs text-fg-3">
                A superadmin keeps it; for a lost phone, use a recovery code or ask another superadmin to reset it.
            </p>
        </template>
        <form v-else-if="step === 'password'" class="flex flex-wrap items-end gap-2" @submit.prevent="start">
            <div>
                <label>Your password</label
                ><input v-model="password" type="password" autocomplete="current-password" data-test="totp-password" />
            </div>
            <button type="submit" class="btn-primary" :disabled="busy || !password">Next</button>
            <button type="button" class="btn-ghost" @click="step = ''">Cancel</button>
        </form>
        <form v-else-if="step === 'scan'" @submit.prevent="enable">
            <p class="mt-0 text-fg-2">Scan this with the app, then type the code it shows.</p>
            <img :src="qr" alt="the QR code to scan with the authenticator app" class="rounded bg-white" />
            <p class="text-xs text-fg-3">
                Or type the key into the app:
                <code class="font-mono select-all" data-test="totp-secret">{{ grouped }}</code>
            </p>
            <div class="flex flex-wrap items-end gap-2">
                <div>
                    <label>Code</label
                    ><input
                        v-model="code"
                        autocomplete="one-time-code"
                        inputmode="numeric"
                        class="w-28 font-mono"
                        placeholder="123456"
                        data-test="totp-enable-code"
                    />
                </div>
                <button type="submit" class="btn-primary" :disabled="busy || !code">Turn on</button>
                <button type="button" class="btn-ghost" @click="step = ''">Cancel</button>
            </div>
        </form>
        <div v-else-if="step === 'codes'">
            <p class="mt-0 text-fg-2">
                Two-factor sign-in is on. Keep these recovery codes somewhere safe: each signs you in once without the
                app (a lost phone). They are not shown again.
            </p>
            <ul class="grid grid-cols-2 gap-x-4 font-mono text-sm" data-test="totp-recovery">
                <li v-for="c in codes" :key="c">{{ c }}</li>
            </ul>
            <div class="mt-2 flex gap-2">
                <button type="button" class="btn-ghost" @click="copyCodes">Copy</button>
                <button type="button" class="btn-primary" data-test="totp-done" @click="finish">
                    I have saved them
                </button>
            </div>
        </div>
        <form v-else-if="step === 'off'" class="flex flex-wrap items-end gap-2" @submit.prevent="disable">
            <div>
                <label>Your password</label><input v-model="password" type="password" autocomplete="current-password" />
            </div>
            <div>
                <label>A code</label
                ><input v-model="code" autocomplete="one-time-code" class="w-28 font-mono" placeholder="123456" />
            </div>
            <button type="submit" class="btn-danger" :disabled="busy || !password || !code">Turn off</button>
            <button type="button" class="btn-ghost" @click="step = ''">Cancel</button>
        </form>
        <p v-if="err" class="mt-2 mb-0 text-bad">{{ err }}</p>
    </div>
</template>
